import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import type {
  JobBoard,
  JobBoardPayloadParams,
  JobBoardPosting,
  JobBoardPublishOfferParams,
  JobBoardsCounters,
} from '@recruitee/job-boards-types';

import type { BulkExecutionResult, OfferJobBoardEntity } from './offer-job-board.types';

const {
  actions,
  namespace,
  selectors: baseSelectors,
  reducer,
} = createEntityCollectionStore<OfferJobBoardEntity>()('OfferJobBoards', {
  idSelector: (jobBoard: OfferJobBoardEntity) => jobBoard.storeId,
  customActions: adapter => ({
    fetchPaginated: (state, payload: { scope: string; params?: JobBoardPayloadParams }) =>
      adapter.markAsPending(state, { scope: payload.scope, entities: [] }),
    fetchPaginatedSuccess: (state, payload: { data: OfferJobBoardEntity[]; scope: string }) =>
      pipeState(
        state,
        next => adapter.upsertMany(next, payload.data, { scope: payload.scope }),
        next =>
          adapter.markAsLoaded(next, {
            scope: payload.scope,
            entities: payload.data.map(e => e.storeId),
          }),
      ),
    fetchPaginatedFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    fetchCounters: ngrxEvent<{ id: number }>(),
    fetchCountersSuccess: ngrxEvent<{ id: number; counters: JobBoardsCounters }>(),
    fetchCountersFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
    submit: ngrxEvent<{ codename: string; params: JobBoardPublishOfferParams }>(),
    submitSuccess: ngrxEvent<{ posting: JobBoardPosting }>(),
    submitFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    resubmit: ngrxEvent<{ codename: string; postingId: number }>(),
    resubmitSuccess: ngrxEvent<{ posting: JobBoardPosting }>(),
    resubmitFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    withdraw: ngrxEvent<{ codename: string; postingId: number }>(),
    withdrawSuccess: ngrxEvent<{ posting: JobBoardPosting }>(),
    withdrawFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    bulkSubmit: ngrxEvent<{
      jobId: number;
      data: Record<string, { credentialId?: number; posterEmail?: string; jobBoard: JobBoard }>;
    }>(),
    bulkSubmitSuccess: ngrxEvent<BulkExecutionResult>(),
    bulkWithdraw: ngrxEvent<{ jobBoards: JobBoard[] }>(),
    bulkWithdrawSuccess: ngrxEvent<BulkExecutionResult>(),
  }),
});

const selectors = {
  ...baseSelectors,
  counters: memoizeSelector((props: { scope: string }) =>
    createSelector(baseSelectors.entities(props), jobBoards =>
      jobBoards.reduce(
        (acc, jobBoard) => {
          acc.all = acc.all + 1;
          if (!!jobBoard.posting) acc.published = acc.published + 1;

          return acc;
        },
        { all: 0, published: 0 },
      ),
    ),
  ),
};

export {
  actions as OfferJobBoardActions,
  namespace as OfferJobBoardNamespace,
  reducer as OfferJobBoardReducer,
  selectors as OfferJobBoardSelectors,
};
